import type { DrawPick, ObservedDraw } from '../domain/dice.ts';
import { drawsForTurn, readingsForDraw } from '../domain/diceTrace.ts';
import type { TurnResult } from '../domain/turn.ts';
import { churnLine } from '../domain/churnStory.ts';

export interface TableDie { face: number; sides: number; role: string }
export interface TableRoll {
  id: string;
  rawFaces: readonly number[];
  selection?: number;
  title: string;
  instruction: string;
  /** Plain-English answer to "why is this die rolling?", shown before it moves. */
  purpose: string;
  dice: TableDie[];
  read: string;
  citation: string;
  outcome?: string;
  entry?: 'hand' | 'engine' | 'mixed';
  /**
   * A throw of the BOTTOM GROUP CHURN prelude (EZRA-S2 §3.12), including the
   * d10 that checks for one before every green turn. Howard, 5 Oct 2026: "I
   * hate that moment and the repetitive rolls". These throws are still recorded
   * and still decide the back of the field; the table just never walks them.
   */
  churn?: boolean;
  /** On the first Churn throw of a turn whose Churn happened: what it did, in one line. */
  churnLine?: string;
  /**
   * WHO THE THROW IS FOR, known before it lands (Howard, 7 Oct 2026: "use the
   * actual driver cards when they are called"). A Fast Pit check, a draft, an
   * ESCAPE check is rolled FOR a named driver; a selection is rolled over a
   * named pool. Safe to show while the dice are still in the air.
   */
  subjectIds: string[];
  /**
   * Everyone the throw turned out to be about: the subject plus every driver
   * named by the rule it resolved. Shown only once the throw is read — the
   * rest is a consequence, and consequences wait for their dice.
   */
  involvedIds: string[];
  /** A selection over a named pool: the driver the face picked. */
  pickedId?: string;
}

/** What each kind of uniform selection is choosing, for the table's words. */
const PICKS: Record<DrawPick, { title: string; instruction: (n: number) => string; read: (face: number, n: number) => string }> = {
  'trouble-space': {
    title: 'Draw a trouble space',
    instruction: (n) => `This track's spaces for that kind of trouble are used up, so one of the ${n} spaces left on the card is drawn at random.`,
    read: (face, n) => `Space ${face} of ${n}`,
  },
  'hot-head': {
    title: 'Who is the hot-head?',
    instruction: (n) => `${n} drivers carry a grudge into this turn. One of them is drawn to act on it.`,
    read: (face, n) => `Driver ${face} of ${n}`,
  },
  'fume-target': {
    title: 'Who does he take it out on?',
    instruction: (n) => `He's fuming, and any of the ${n} cars in his group could catch it. One is drawn.`,
    read: (face, n) => `Car ${face} of ${n}`,
  },
};

const unique = (ids: readonly string[]): string[] => [...new Set(ids)];

/**
 * Name the decision before the die moves. These words use only the phase,
 * notation and already-known subjects — never the face or its consequence.
 */
function purposeFor(input: {
  phase: string; citation: string; sides: number; role?: string;
  subjectCount: number; pick?: DrawPick; selection: boolean; pit: boolean;
  photoFinish: boolean; cam: boolean; challenge: boolean; code: boolean; sum: boolean;
}): string {
  const { phase, citation, sides, role, subjectCount, pick, selection, pit, photoFinish, cam, challenge, code, sum } = input;
  if (pick === 'trouble-space') return 'Which unused trouble space on the track card is drawn.';
  if (pick === 'hot-head') return 'Which driver with a grudge becomes the hot-head.';
  if (pick === 'fume-target') return 'Which nearby driver becomes the target.';
  if (selection) return 'Which eligible choice is selected.';
  if (photoFinish) return 'Who wins at the line.';
  if (cam) return pit ? 'Which pit-road group acts, and whether the boxes switch or draw a violation.' : 'Which part of the field acts, and what the track card calls for.';
  if (challenge) return 'Whether the challenger has the gas and the defender has the brakes.';
  if (phase.includes('RAIN THREAT')) return 'Whether the rain stops the race.';
  if (phase.includes('RACE START EVENT')) return 'Whether the start or restart triggers a special race-start event.';
  if (phase.includes('BOTTOM GROUP SURGE')) return 'Whether a car from the bottom group gets a surge forward.';
  if (phase.includes('BOTTOM GROUP CHURN')) return 'Whether the back of the field shuffles this turn.';
  if (phase.includes('TRACK TEMPER')) return 'Whether the track stays calm or puts a car in trouble.';
  if (phase.includes('WAVE AROUND')) return 'Which eligible lapped car receives the wave-around.';
  if (phase.includes('FEUD ROLL')) return 'Whether the pit violation also starts a feud.';
  if (phase.includes('DRAFTING')) return 'Whether the driver shown completes the draft.';
  if (phase.includes('FILL A GAP')) return 'Whether the driver shown has enough gas to fill the open position.';
  if (phase.includes('FEUD / FUME')) return 'Who acts on the feud or fume, and what happens next.';
  if (citation.includes('RB/xiii-xiv(4)')) return subjectCount >= 2 ? 'Whether the pit-road challenger shown takes the lead.' : 'Which TOP-group car gets the chance to challenge the leader.';
  if (citation.includes('RB/vii') && sides === 6) return 'Whether tied performance chips are spent or kept: odd spends, even keeps.';
  if (citation.includes('RB/ix')) return 'Whether the driver shown wins this duel.';
  if (citation.includes('RARE RESULTS') || citation.includes('RB/iv')) return 'Which required follow-up on the rare-result chart applies.';
  if (citation.includes('RESOLVING TROUBLE RESULTS') || citation.includes('RB/v')) return 'Which trouble detail applies before the affected cars are resolved.';
  if (sides === 10 && subjectCount > 0) return 'How long the injured driver shown will be sidelined.';
  if (pit && role === 'black' && subjectCount === 0) return 'Which TOP-group box supplies the challenger for the lead.';
  if (pit && subjectCount >= 2) return 'Whether the challenger shown beats the leader on gas.';
  if (pit && subjectCount === 1) return 'Whether the pit-road follow-up affects the driver shown.';
  if (code) return 'Which chart entry applies.';
  if (sum && subjectCount > 0) return 'Whether the driver shown passes the current rating check.';
  if (sum) return 'Which total the current race rule uses.';
  if (subjectCount > 0) return 'Whether the driver shown passes this check.';
  return 'Which required follow-up the current race action takes.';
}

/** The throws the table actually plays: everything but the Churn's. */
export const tableThrows = (rolls: readonly TableRoll[]): TableRoll[] => rolls.filter((r) => !r.churn);

/** Actual draw order, including unlisted selections; ledger records are never resampled. */
export function rollsForTurn(turn: TurnResult | undefined, finish: readonly ObservedDraw[] = []): TableRoll[] {
  if (!turn) return [];
  const draws = [...(drawsForTurn(turn) ?? []), ...finish];
  const groups: ObservedDraw[][] = [];
  for (const draw of draws) {
    const last = groups.at(-1);
    if (last?.[0].throwIndex === draw.throwIndex) last.push(draw);
    else groups.push([draw]);
  }
  const shuffle = churnLine(turn) ?? undefined;
  let toldShuffle = false;
  const churnMark = (isChurn: boolean): Pick<TableRoll, 'churn' | 'churnLine'> => {
    if (!isChurn) return {};
    if (!shuffle || toldShuffle) return { churn: true };
    toldShuffle = true;
    return { churn: true, churnLine: shuffle };
  };
  return groups.map(group => {
    const first = group[0];
    const rawFaces = group.map(d => d.face);
    const phase = first.phase ?? 'Race check';
    const pit = phase.includes('PIT TURN');
    const pfa = phase.includes('PHOTO FINISH');
    const selection = first.notation === 'dN';
    const pick = selection && first.pick ? PICKS[first.pick] : undefined;
    const subjectIds = unique(group.flatMap(d => d.subject ?? []));
    // A throw rolled FOR named drivers is "involved" only with the rulings that
    // touch them. A phase that resolves several rulings at once (the draft) hangs
    // them all on its last die; the other drivers' moves are not this roll's.
    const readings = group.flatMap(readingsForDraw);
    const touching = subjectIds.length ? readings.filter(e => !e.actorIds || e.actorIds.some(id => subjectIds.includes(id))) : readings;
    const actorIds = touching.flatMap(e => e.actorIds ?? []);
    const pickedId = selection && subjectIds.length === first.sides ? subjectIds[first.face - 1] : undefined;
    const sum = first.notation === '2d6-sum' || first.notation === '2d10-sum';
    const cam = first.notation === 'race-cam';
    const code = first.notation === '11-66';
    const challenge = first.notation === 'challenge';
    const phaseTitle = phase.includes('RAIN THREAT') ? 'Rain check' : phase.includes('FEUD ROLL') ? 'Feud roll' : phase.includes('TRACK TEMPER') ? 'Track temper' : phase.includes('BOTTOM GROUP CHURN') ? 'Bottom group churn' : phase.includes('BOTTOM GROUP SURGE') ? 'Bottom group surge' : phase.includes('RACE START EVENT') ? 'Race start event' : phase.includes('DRAFTING') ? 'Drafting check' : phase.includes('FILL A GAP') ? 'Fill the gap' : phase.includes('FEUD / FUME') ? 'Feud or fume' : null;
    const title = pick ? pick.title : selection ? 'Choose from the eligible group' : pfa ? 'At the line' : cam ? pit ? 'Pit road' : 'Race cam' : challenge ? 'Quality challenge' : code ? 'Chart lookup' : sum ? 'Add the dice' : phaseTitle ?? (first.sides === 10 ? 'Ten-sided check' : 'Single-die check');
    const citation = group.flatMap(readingsForDraw).map(e => e.ruleCitation).join(' · ') || phase;
    return {
      id: `${turn.turn}-throw-${first.throwIndex}`, rawFaces,
      selection: selection ? first.face : undefined,
      title,
      instruction: pick ? pick.instruction(first.sides) : selection ? `A retained selection from ${first.sides} eligible choices.`
        : cam ? pit ? 'Black selects the crews. The metallic pair determines the box switches or violations.' : 'Black selects the group. Add the two metallic dice to read the track card.'
        : challenge ? 'Red checks gas. White selects the defender. Blue checks brakes.'
        : code ? 'Read red first, then white. Keep the two digits in that order.'
        : sum ? 'Add the faces for this check.' : first.sides === 10 ? 'Read 1 through 10. The 10 is ten, not zero.' : 'Read the face for the current check.',
      purpose: purposeFor({ phase, citation, sides: first.sides, role: first.role, subjectCount: subjectIds.length, pick: first.pick, selection, pit, photoFinish: pfa, cam, challenge, code, sum }),
      dice: selection ? [] : group.map(d => ({face:d.face,sides:d.sides,role:d.role && !['first','second'].includes(d.role) ? d.role : 'metallic'})),
      read: pick ? pick.read(first.face, first.sides) : selection ? `Selection ${first.face} of ${first.sides}`
        : code ? `Chart ${rawFaces.join('')}`
        : cam ? `Black ${rawFaces[0]} · metallic total ${rawFaces[1] + rawFaces[2]}`
        : sum ? `Total ${rawFaces.reduce((a,b) => a+b,0)}` : rawFaces.join(' · '),
      citation,
      outcome: group.flatMap(readingsForDraw).map(e => e.description).join(' ') || undefined,
      entry: group.every(d => d.entry === first.entry) ? first.entry : 'mixed',
      ...churnMark(phase.includes('BOTTOM GROUP CHURN')),
      subjectIds,
      // A selection's pool is the eligible group; once it lands, the one picked
      // leads, and the rule's own actors follow.
      involvedIds: pickedId ? unique([pickedId, ...actorIds]) : unique([...subjectIds, ...actorIds]),
      ...(pickedId ? { pickedId } : {}),
    };
  });
}

export const TABLE_PREF = 'rwbr.tabletop.preferences.v1';

/**
 * HOW LONG A READ STAYS UP (Howard, 7 Oct 2026: "the cards and results of a roll
 * and stuff for me to read are too fast to disappear. Is there a setting for
 * that time frame?"). Before this, every read held 1.2 s and a commissioner
 * result 2.6 s, whatever there was to read.
 *
 *   quick  — the old fixed times.
 *   steady — the default: the hold grows with the words on screen (about a
 *            relaxed reading pace) and with any driver cards to look at.
 *   slow   — steady × 1.6.
 *   wait   — a read with something in it stays until "Next roll".
 *
 * A throw with nothing to read (no outcome line, no cards) never waits long in
 * any setting — holding an empty "4" on screen is not reading time, it's a stall.
 * Presentation only: nothing here can change a roll.
 */
export type TableHold = 'quick' | 'steady' | 'slow' | 'wait';
export const TABLE_HOLDS: readonly { key: TableHold; label: string }[] = [
  { key: 'quick', label: 'Quick (about a second)' },
  { key: 'steady', label: 'Steady (longer when there is more to read)' },
  { key: 'slow', label: 'Slow' },
  { key: 'wait', label: 'Wait for me (press Next)' },
];
const isHold = (v: unknown): v is TableHold => TABLE_HOLDS.some((h) => h.key === v);

export const QUICK_READ_MS = 1200;
export const QUICK_RESULT_MS = 2600;
/** Words per second of a relaxed read, as a per-word hold. */
const PER_WORD_MS = 220;
const wordsIn = (text: string) => text.split(/\s+/).filter(Boolean).length;

/** How long one throw's read stays up before the next throw; null = until Next. */
export function readingMs(hold: TableHold, text: string, cards: number): number | null {
  const words = wordsIn(text);
  const something = words > 2 || cards > 0;
  if (hold === 'quick' || !something) return QUICK_READ_MS;
  if (hold === 'wait') return null;
  const steady = 1800 + PER_WORD_MS * words + (cards > 0 ? 1200 : 0);
  return hold === 'slow' ? Math.min(16000, Math.round(steady * 1.6)) : Math.min(10000, steady);
}

/** How long a commissioner throw's result holds before the race runs on; null = until Race on. */
export function resultMs(hold: TableHold, text: string, cards: number): number | null {
  if (hold === 'quick') return QUICK_RESULT_MS;
  if (hold === 'wait') return null;
  const steady = 3000 + PER_WORD_MS * wordsIn(text) + (cards > 0 ? 1500 : 0);
  return hold === 'slow' ? Math.min(18000, Math.round(steady * 1.6)) : Math.min(12000, steady);
}

/** Between the follow-on dice of a commissioner moment (each may show its driver's card). */
export function followOnMs(hold: TableHold): number {
  return hold === 'quick' ? 420 : hold === 'steady' ? 700 : 1000;
}

export interface TablePreferences { animated: boolean; sound: boolean; hold: TableHold }
export const DEFAULT_TABLE: TablePreferences = { animated: true, sound: false, hold: 'steady' };
export function readTablePreferences(): TablePreferences {
  try { const raw = JSON.parse(localStorage.getItem(TABLE_PREF) ?? '{}'); return {
    animated: typeof raw.animated === 'boolean' ? raw.animated : true,
    sound: typeof raw.sound === 'boolean' ? raw.sound : false,
    hold: isHold(raw.hold) ? raw.hold : DEFAULT_TABLE.hold,
  }; } catch { return {...DEFAULT_TABLE}; }
}
export function saveTablePreferences(value: TablePreferences) {
  try { localStorage.setItem(TABLE_PREF, JSON.stringify(value)); } catch { /* Playback works without storage. */ }
}
